import { ApolloQueryResult, PureQueryOptions, ApolloError } from '@apollo/client';
import axios from 'axios';
import {
  AgFinancing,
  AgFinancingFile,
  AgFinancingFilesContext,
  AgFinancingFileFolder,
  AgFinancingDocumentType
} from '@indigo-ag/schema';

import { RemoteFinancingDocumentInfo } from 'shared';
import {
  removeFinancingAgFinancingFiles,
  getAgFinancingPresignedUploadUrlMultiple
} from 'features/brazil-grower/queries';
import { getClient } from 'core/apollo/client';
import { Dictionary } from 'lodash';
import { Logger } from '@indigo-ag/ui-core';

export interface IUploadFilesToAgFinancingOptions {
  agFinancingId: AgFinancing['id'];
  folder: AgFinancingFileFolder;
}
export interface MultipleFileUploadResult {
  fulfilled: Partial<AgFinancingFile>[];
  rejected: RejectedPromiseReason[];
}
export interface RejectedPromiseReason {
  documentName: string;
  error: unknown;
}

export type ProgressDictionary = Dictionary<number>;

export const uploadFilesToAgFinancing = (options: IUploadFilesToAgFinancingOptions) => {
  return async (
    files: File[],
    context: AgFinancingFilesContext,
    documentType: AgFinancingDocumentType | Dictionary<AgFinancingDocumentType> = AgFinancingDocumentType.OTHER,
    onUploadProgress?: (progressDictionary: ProgressDictionary) => void
  ): Promise<MultipleFileUploadResult> => {
    const client = await getClient();
    let progressDictionary: ProgressDictionary = files.reduce(
      // tslint:disable:no-string-literal
      (acc, current) => ({ ...acc, [current['newFileId']]: 0 }),
      {}
    );
    return client
      .query({
        fetchPolicy: 'no-cache',
        query: getAgFinancingPresignedUploadUrlMultiple,
        variables: {
          data: files.map(f => ({
            contentType: f.type,
            agFinancingId: options.agFinancingId,
            folder: options.folder
          }))
        }
      })
      .then(
        (
          remoteDocumentsInfo: ApolloQueryResult<{
            financingAgFinancingPresignedUploadUrlMultiple: RemoteFinancingDocumentInfo[];
          }>
        ) => {
          const docsToUpdate: Promise<Partial<AgFinancingFile>>[] = files.map((f, index) => {
            const signedUrl =
              remoteDocumentsInfo.data?.financingAgFinancingPresignedUploadUrlMultiple[index]?.presignedUrl;
            const documentUrl =
              remoteDocumentsInfo.data?.financingAgFinancingPresignedUploadUrlMultiple[index]?.remoteFileName;
            const iOptions = {
              headers: {
                'Content-Type': f.type
              },
              onUploadProgress: (progressEvent: any) => {
                if (onUploadProgress) {
                  progressDictionary = {
                    ...progressDictionary,
                    // tslint:disable:no-string-literal
                    [f['newFileId']]: Math.round((progressEvent.loaded * 100) / progressEvent.total)
                  };
                  onUploadProgress({ ...progressDictionary });
                }
              }
            };
            return axios
              .put(signedUrl, f, iOptions)
              .then(() => ({
                context,
                documentName: f.name,
                documentUrl,
                documentType: typeof documentType === 'string' ? documentType : documentType[f['newFileId']],
                metadata: {
                  // tslint:disable:no-string-literal
                  comment: f['comment']
                }
              }))
              .catch(e => {
                return Promise.reject({
                  documentName: f.name,
                  error: e
                });
              });
          });
          return Promise.allSettled(docsToUpdate).then(results => {
            const fulfilledResults: MultipleFileUploadResult['fulfilled'] = [];
            const rejectedResults: RejectedPromiseReason[] = [];

            results.forEach(r => {
              if (r.status === 'fulfilled') {
                fulfilledResults.push(r.value);
              } else {
                rejectedResults.push(r.reason);
              }
            });

            return {
              fulfilled: fulfilledResults,
              rejected: rejectedResults
            };
          });
        }
      );
  };
};

export const removeFilesFromAgFinancing = async (
  filesIds: AgFinancingFile['id'][],
  refetchQueries: PureQueryOptions[] = []
): Promise<any> => {
  const client = await getClient();

  if (filesIds.length) {
    return client
      .mutate({
        mutation: removeFinancingAgFinancingFiles,
        variables: {
          fileIds: filesIds
        },
        refetchQueries
      })
      .catch((e: ApolloError) => {
        Logger.info(`Error removing files with IDs ${filesIds} - ${e}`);
        throw e;
      });
  } else {
    return Promise.resolve();
  }
};

export const agFinancingFilesToFiles = (agFinancingFiles: Partial<AgFinancingFile>[] = []): File[] => {
  return agFinancingFiles.map(f => {
    const file = new File([], f.documentName!);
    file['comment'] = !f['isNew'] ? f.metadata?.comment : f['comment'];
    file['fileId'] = f.id;
    file['isNew'] = f['isNew'];
    file['newFileId'] = f['newFileId'];
    return file;
  });
};

export const sortFinancingFilesByDateAsc = (agFinancingFiles: AgFinancingFile[]): AgFinancingFile[] => {
  return (
    agFinancingFiles
      // * In case array comes directly from graphql (read-only), we need to create a new files array to be able to sort it
      .map(f => ({ ...f }))
      .sort((a, b) => {
        if (b.loadedAt && a.loadedAt) {
          return new Date(b.loadedAt).getTime() - new Date(a.loadedAt).getTime();
        } else {
          return 0;
        }
      })
  );
};

export const updateFilesAgFinancingFiles = (
  file: File,
  allFiles: Partial<AgFinancingFile>[],
  filesUpdated: AgFinancingFile[],
  setFilesUpdated: (files: AgFinancingFile[]) => void
) => {
  const filesOnState = allFiles.find(fileInAllFiles => fileInAllFiles.id === file['fileId']) as AgFinancingFile;
  const fileUpdated: AgFinancingFile = {
    ...filesOnState,
    metadata: {
      comment: file['comment']
    }
  };
  if (filesUpdated.length === 0) {
    setFilesUpdated([fileUpdated]);
    return;
  }
  const position = filesUpdated.findIndex(iFile => iFile.id === fileUpdated.id);
  if (position !== -1) {
    filesUpdated[position] = fileUpdated;
    setFilesUpdated([...filesUpdated]);
  } else {
    setFilesUpdated([...filesUpdated, fileUpdated]);
  }
};
